import React, { useEffect, useRef, useState } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import BucketRetainConfirmation from './BucketRetainConfirmation';
import PaginatedTable from './PaginatedTable';
import { useCache } from '../hooks/useCache';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useSearchIndexRevalidation } from '../hooks/useSearchIndexRevalidation';

interface Bucket {
  bucketName: string;
  description: string;
  createdAt: string;
  imported?: boolean;
  searchEnabled?: boolean;
  objectCount?: number | null;
  totalSize?: number | null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1_073_741_824) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
}

const BucketManagement: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [showImportForm, setShowImportForm] = useState<boolean>(false);
  const [importBucket, setImportBucket] = useState<{ bucketName: string; description: string }>({
    bucketName: '',
    description: '',
  });
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const [importNameError, setImportNameError] = useState<string>('');
  const [editingBucket, setEditingBucket] = useState<Bucket | null>(null);
  const [newBucket, setNewBucket] = useState({ bucketName: '', description: '' });
  const [deletingBucketMeta, setDeletingBucketMeta] = useState<{ bucketName: string; isImported: boolean } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [togglingSearch, setTogglingSearch] = useState<string | null>(null);
  const [bucketStatsOverrides, setBucketStatsOverrides] = useState<Record<string, { objectCount: number; totalSize: number }>>({});
  const autoDisabledNotifiedRef = useRef<Set<string>>(new Set());

  const BUCKET_NAME_REGEX = /^[a-z0-9][a-z0-9.-]*[a-z0-9]$/;

  // Use cache for buckets data — Task 8.7: guard response
  const { 
    data: buckets = [], 
    loading, 
    refresh: refreshBuckets 
  } = useCache(
    'admin-buckets',
    async () => {
      const response = await apiCall('/admin/buckets');
      return Array.isArray(response) ? response : (response?.buckets ?? []);
    },
    { ttl: 60 * 1000 } // 1 minute cache for bucket data
  );

  const createBucket = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiCall('/admin/buckets', {
        method: 'POST',
        body: JSON.stringify(newBucket)
      });
      
      toast.success('Bucket created successfully');
      setNewBucket({ bucketName: '', description: '' });
      setShowCreateForm(false);
      refreshBuckets();
    } catch (error: unknown) {
      console.error('Error creating bucket:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create bucket');
    }
  };

  const importBucketHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    const { bucketName, description } = importBucket;
    const trimmed = bucketName.trim();

    if (!BUCKET_NAME_REGEX.test(trimmed) || trimmed.length < 3 || trimmed.length > 63) {
      setImportNameError(
        'Bucket name must be 3–63 characters, lowercase letters, numbers, hyphens, and dots only, and must start and end with a letter or number.'
      );
      return;
    }
    setImportNameError('');
    setImportLoading(true);

    try {
      await apiCall('/admin/external-buckets', {
        method: 'POST',
        body: JSON.stringify({ bucketName: trimmed, description }),
      });
      toast.success('Bucket imported successfully');
      setImportBucket({ bucketName: '', description: '' });
      setShowImportForm(false);
      refreshBuckets();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to import bucket');
    } finally {
      setImportLoading(false);
    }
  };

  const updateBucket = async (bucketName: string, description: string) => {
    try {
      await apiCall(`/admin/buckets/${bucketName}`, {
        method: 'PUT',
        body: JSON.stringify({ description })
      });
      
      toast.success('Bucket updated successfully');
      setEditingBucket(null);
      refreshBuckets();
    } catch (error) {
      console.error('Error updating bucket:', error);
      toast.error('Failed to update bucket');
    }
  };

  const handleDeregister = async (bucketName: string) => {
    setDeleteLoading(true);
    try {
      await apiCall(`/admin/buckets/${bucketName}?retain=true`, { method: 'DELETE' });
      toast.success('Bucket deregistered successfully');
      setDeletingBucketMeta(null);
      refreshBuckets();
    } catch (error: any) {
      toast.error(error.message || 'Failed to deregister bucket');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDestructiveDelete = async (bucketName: string) => {
    setDeleteLoading(true);
    try {
      await apiCall(`/admin/buckets/${bucketName}`, { method: 'DELETE' });
      toast.success('Bucket deleted successfully');
      setDeletingBucketMeta(null);
      refreshBuckets();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete bucket');
      // Keep modal open on 409 (non-empty bucket)
    } finally {
      setDeleteLoading(false);
    }
  };

  const getBucketAnalytics = async (bucketName: string) => {
    try {
      const response = await apiCall(`/admin/bucket-analytics/${bucketName}`);
      // Update the table row immediately with the fresh metrics
      setBucketStatsOverrides(prev => ({
        ...prev,
        [bucketName]: { objectCount: response.fileCount, totalSize: response.totalSize },
      }));
      toast.success(`${bucketName}: ${response.fileCount} files, ${(response.totalSize / (1024*1024)).toFixed(2)} MB total`);
    } catch (error) {
      toast.error('Failed to get bucket analytics');
    }
  };

  const toggleSearchEnabled = async (bucket: Bucket) => {
    const newValue = !(bucket.searchEnabled ?? true);
    setTogglingSearch(bucket.bucketName);
    try {
      await apiCall(`/admin/bucket-search/${bucket.bucketName}`, {
        method: 'PUT',
        body: JSON.stringify({ searchEnabled: newValue }),
      });
      toast.success(`Search ${newValue ? 'enabled' : 'disabled'} for "${bucket.bucketName}"`);
      refreshBuckets();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to update search setting');
    } finally {
      setTogglingSearch(null);
    }
  };

  // Notify admin of any buckets that have been auto-disabled due to density.
  // The searchAutoDisabled flag is persisted in DynamoDB by the bucket-metrics handler
  // when objectCount >= 300000; here we surface it once per session for each affected bucket.
  useEffect(() => {
    if (!Array.isArray(buckets)) return;
    buckets.forEach((bucket: Bucket) => {
      if (
        bucket.searchEnabled === false &&
        bucket.objectCount != null &&
        bucket.objectCount >= 300000 &&
        !autoDisabledNotifiedRef.current.has(bucket.bucketName)
      ) {
        autoDisabledNotifiedRef.current.add(bucket.bucketName);
        toast(
          `Search has been automatically disabled for "${bucket.bucketName}" because it has ≥ 300,000 objects (Dense Bucket).`,
          { icon: '⚠️' }
        );
      }
    });
  }, [buckets]);

  const updateBucketCors = async (bucketName: string) => {
    try {
      await apiCall(`/admin/bucket-cors/${bucketName}`, {
        method: 'PUT'
      });
      toast.success(`CORS configuration updated for "${bucketName}"`);
    } catch (error) {
      console.error('Error updating CORS:', error);
      toast.error(`Failed to update CORS for "${bucketName}"`);
    }
  };

/** Inner component so the revalidation hook can be called at the top level. */
interface RefreshIndexButtonProps { bucketName: string; }
const RefreshIndexButton: React.FC<RefreshIndexButtonProps> = ({ bucketName }) => {
  const { isRefreshing, isRateLimited, revalidate, timeUntilLabel, lastSyncedAt } = useSearchIndexRevalidation(bucketName);
  return (
    <button
      onClick={() => revalidate(bucketName)}
      disabled={isRefreshing || isRateLimited}
      title={isRateLimited ? `Next sync available in ${timeUntilLabel}` : lastSyncedAt ? `Last synced: ${lastSyncedAt.toLocaleString()}` : 'Rebuild the search index for this bucket'}
      className="text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isRefreshing ? 'Refreshing...' : isRateLimited ? `Index synced (${timeUntilLabel})` : 'Refresh Index'}
    </button>
  );
};

  const columns = [
    {
      key: 'bucketName',
      header: 'Bucket Name',
      sortable: true,
      render: (bucket: Bucket) => (
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-900">{bucket.bucketName}</span>
          {bucket.imported && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
              Imported
            </span>
          )}
        </div>
      )
    },
    {
      key: 'description',
      header: 'Description',
      render: (bucket: Bucket) => (
        editingBucket?.bucketName === bucket.bucketName ? (
          <div>
            <label
              htmlFor={`edit-description-${bucket.bucketName}`}
              className="sr-only"
            >
              Description for {bucket.bucketName}
            </label>
            <textarea
              id={`edit-description-${bucket.bucketName}`}
              value={editingBucket.description}
              onChange={(e) => setEditingBucket({ ...editingBucket, description: e.target.value })}
              className="w-full border-gray-300 rounded-md text-sm p-2"
              rows={2}
              placeholder="Enter description..."
            />
          </div>
        ) : (
          <span className="text-sm text-gray-600">
            {bucket.description || 'No description'}
          </span>
        )
      )
    },
    {
      key: 'objectCount',
      header: 'Objects',
      render: (bucket: Bucket) => {
        if (bucket.objectCount == null) return <span className="text-gray-400">—</span>;
        const isDense = bucket.objectCount >= 300000;
        return (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-900">~{bucket.objectCount.toLocaleString()}</span>
            {isDense && (
              <span className="bg-yellow-50 border border-yellow-200 text-yellow-700 text-xs px-2 py-0.5 rounded">
                Dense bucket
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'totalSize',
      header: 'Size',
      render: (bucket: Bucket) => {
        if (bucket.totalSize == null) return <span className="text-gray-400">—</span>;
        return (
          <span
            className="text-sm text-gray-900"
            title="Includes all object versions and incomplete uploads"
          >
            ~{formatFileSize(bucket.totalSize)}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (bucket: Bucket) => (
        <span className="text-sm text-gray-500">
          {new Date(bucket.createdAt).toLocaleDateString()}
        </span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Bucket Management</h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { setShowImportForm(!showImportForm); setShowCreateForm(false); }}
            className="btn-secondary flex items-center space-x-2 px-4 py-2"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            <span>Import Existing Bucket</span>
          </button>
          <button
            onClick={() => { setShowCreateForm(!showCreateForm); setShowImportForm(false); }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            {showCreateForm ? 'Cancel' : 'Add Bucket'}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Bucket</h3>
          <form onSubmit={createBucket} className="space-y-4">
            <div>
              <label htmlFor="create-bucket-name" className="block text-sm font-medium text-gray-700 mb-1">Bucket Name</label>
              <input
                id="create-bucket-name"
                type="text"
                value={newBucket.bucketName}
                onChange={(e) => setNewBucket({ ...newBucket, bucketName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="my-s3-bucket"
                pattern="[a-z0-9.-]+"
                title="Bucket name must contain only lowercase letters, numbers, dots, and hyphens"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Must be globally unique and follow S3 naming rules</p>
            </div>
            
            <div>
              <label htmlFor="create-bucket-description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                id="create-bucket-description"
                value={newBucket.description}
                onChange={(e) => setNewBucket({ ...newBucket, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Optional description for this bucket"
              />
            </div>
            
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Create Bucket
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {showImportForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Import Existing Bucket</h3>
          <form onSubmit={importBucketHandler} className="space-y-4">
            <div>
              <label htmlFor="importBucketName" className="block text-sm font-medium text-gray-700 mb-1">
                Bucket Name
              </label>
              <input
                id="importBucketName"
                type="text"
                value={importBucket.bucketName}
                onChange={(e) => setImportBucket({ ...importBucket, bucketName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="my-existing-bucket"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the exact S3 bucket name as it appears in AWS
              </p>
              {importNameError && (
                <p className="mt-1 text-xs text-red-600">{importNameError}</p>
              )}
            </div>
            <div>
              <label htmlFor="importDescription" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="importDescription"
                value={importBucket.description}
                onChange={(e) => setImportBucket({ ...importBucket, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Optional description for this bucket"
              />
            </div>
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={importLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {importLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                )}
                {importLoading ? 'Importing...' : 'Import Bucket'}
              </button>
              <button
                type="button"
                onClick={() => setShowImportForm(false)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <PaginatedTable
        data={(buckets || []).map((b: Bucket) => {
          const override = bucketStatsOverrides[b.bucketName];
          return override ? { ...b, objectCount: override.objectCount, totalSize: override.totalSize } : b;
        })}
        columns={columns}
        loading={loading}
        pageSize={10}
        searchPlaceholder="Search buckets by name, description..."
        emptyMessage="No buckets found"
        actions={(bucket: Bucket) => (
          <div className="flex items-center space-x-2">
            {editingBucket?.bucketName === bucket.bucketName ? (
              <>
                <button
                  onClick={() => updateBucket(bucket.bucketName, editingBucket.description)}
                  className="text-green-600 hover:text-green-800 text-sm font-medium"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingBucket(null)}
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => getBucketAnalytics(bucket.bucketName)}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  Analytics
                </button>
                <button
                  onClick={() => updateBucketCors(bucket.bucketName)}
                  className="text-green-600 hover:text-green-800 text-sm font-medium"
                  title="Update CORS configuration for current domain"
                >
                  Update CORS
                </button>
                <RefreshIndexButton bucketName={bucket.bucketName} />
                <button
                  onClick={() => toggleSearchEnabled(bucket)}
                  disabled={togglingSearch === bucket.bucketName}
                  aria-label={
                    (bucket.searchEnabled ?? true)
                      ? `Search enabled for ${bucket.bucketName}`
                      : `Search disabled for ${bucket.bucketName}`
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                    (bucket.searchEnabled ?? true) ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      (bucket.searchEnabled ?? true) ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <button
                  onClick={() => setEditingBucket(bucket)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => setDeletingBucketMeta({ bucketName: bucket.bucketName, isImported: bucket.imported === true })}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        )}
      />

      {deletingBucketMeta && (
        <BucketRetainConfirmation
          bucketName={deletingBucketMeta.bucketName}
          isImported={deletingBucketMeta.isImported}
          loading={deleteLoading}
          onDeregister={handleDeregister}
          onDestructiveDelete={handleDestructiveDelete}
          onCancel={() => setDeletingBucketMeta(null)}
        />
      )}
      {/* BucketDeleteConfirmation is kept importable for backward compatibility */}
    </div>
  );
};

export default BucketManagement;